"use client";

/**
 * Second step of signing in on a new device: type the 6-digit code we emailed.
 *
 * Shown by the login page when /api/auth/signin answers requiresStepUp. Works the same inside
 * the phone apps, which load this page from growth-sync.com in a web view: no deep link is
 * involved, the member copies the code from their mail app (iOS also offers it above the
 * keyboard thanks to autocomplete="one-time-code").
 *
 * Accessibility: a real labelled input, the hint and any error tied to it with
 * aria-describedby, errors announced with role="alert", progress messages with aria-live, and
 * every control at least 44px tall.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/lib/api/utils";
import { useTranslations } from "@/hooks/use-translations";

export interface EmailStepSession {
  access_token: string;
  refresh_token: string;
}

interface Props {
  challengeToken: string;
  emailHint: string;
  codeSent: boolean;
  resendInSeconds: number;
  /** Called with the new session once the code is accepted. Should set it and navigate. */
  onVerified: (session: EmailStepSession, user: { id: string; email: string }) => Promise<void>;
  /** Back to the password form, optionally with a message to show there. */
  onRestart: (message?: string) => void;
}

const CODE_LENGTH = 6;

export function EmailCodeStep({ challengeToken, emailHint, codeSent, resendInSeconds, onVerified, onRestart }: Props) {
  const { t } = useTranslations();
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const submittingRef = useRef(false);

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(codeSent ? resendInSeconds : 0);
  const [error, setError] = useState<string | null>(codeSent ? null : t("auth.emailStep.sendFailed"));
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // The input is disabled while a code is being checked, and a disabled field cannot take focus,
  // so put the cursor back once it is enabled again after a wrong code.
  useEffect(() => {
    if (!busy && error) inputRef.current?.focus();
  }, [busy, error]);

  /** Server error codes to translated copy. Falls back to the server's own plain sentence. */
  const messageFor = useCallback(
    (data: { errorCode?: string; error?: string; attemptsLeft?: number }): string => {
      switch (data.errorCode) {
        case "wrong_code":
          return typeof data.attemptsLeft === "number"
            ? `${t("auth.emailStep.wrongCode")} ${t("auth.emailStep.attemptsLeft", { count: data.attemptsLeft })}`
            : t("auth.emailStep.wrongCode");
        case "code_expired":
          return t("auth.emailStep.codeExpired");
        case "invalid_code":
          return t("auth.emailStep.invalidCode");
        case "rate_limited":
        case "cooldown":
          return t("auth.emailStep.rateLimited");
        case "send_failed":
          return t("auth.emailStep.sendFailed");
        case "too_many_attempts":
        case "used":
        case "challenge_expired":
        case "challenge_not_found":
        case "send_limit":
        case "session_failed":
          return t("auth.emailStep.startAgain");
        default:
          return data.error || t("auth.emailStep.generic");
      }
    },
    [t]
  );

  const submit = useCallback(
    async (value: string) => {
      if (submittingRef.current) return;
      if (value.length !== CODE_LENGTH) {
        setError(t("auth.emailStep.invalidCode"));
        return;
      }
      submittingRef.current = true;
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        const response = await fetch(getApiUrl("/api/auth/login-code/verify"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include", // the remembered-device cookie comes back on this response
          body: JSON.stringify({ challengeToken, code: value }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success || !data.session || !data.user) {
          const message = messageFor(data ?? {});
          if (data?.restart) {
            onRestart(message);
            return;
          }
          setError(message);
          setCode("");
          return;
        }
        await onVerified(data.session, data.user);
      } catch {
        setError(t("auth.emailStep.network"));
      } finally {
        submittingRef.current = false;
        setBusy(false);
      }
    },
    [challengeToken, messageFor, onRestart, onVerified, t]
  );

  const resend = async () => {
    if (cooldown > 0 || resending) return;
    setResending(true);
    setError(null);
    setNotice(null);
    try {
      const response = await fetch(getApiUrl("/api/auth/login-code/resend"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ challengeToken }),
      });
      const data = await response.json().catch(() => ({}));
      if (typeof data?.resendInSeconds === "number") setCooldown(data.resendInSeconds);
      if (!response.ok || !data?.success) {
        const message = messageFor(data ?? {});
        if (data?.restart) {
          onRestart(message);
          return;
        }
        setError(message);
        return;
      }
      setCode("");
      setNotice(t("auth.emailStep.resent"));
      inputRef.current?.focus();
    } catch {
      setError(t("auth.emailStep.network"));
    } finally {
      setResending(false);
    }
  };

  const onChange = (raw: string) => {
    // Paste-friendly: "123 456", "123-456" and a trailing newline all become 123456.
    const digits = raw.replace(/\D/g, "").slice(0, CODE_LENGTH);
    setCode(digits);
    if (error) setError(null);
    // Finish as soon as the sixth digit lands (typed, pasted or autofilled), once.
    if (digits.length === CODE_LENGTH) void submit(digits);
  };

  return (
    <form
      className="space-y-5"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit(code);
      }}
    >
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <MailCheck className="h-6 w-6 shrink-0 text-primary" aria-hidden="true" />
          <h2 className="gs-h2 text-neutral-900">{t("auth.emailStep.title")}</h2>
        </div>
        <p className="text-sm leading-relaxed text-neutral-700 md:text-base">
          {codeSent || notice
            ? t("auth.emailStep.sentTo", { email: emailHint })
            : t("auth.emailStep.willSendTo", { email: emailHint })}
        </p>
      </div>

      <div className="space-y-2">
        <label htmlFor={inputId} className="block text-sm font-medium text-neutral-800">
          {t("auth.emailStep.codeLabel")}
        </label>
        <input
          ref={inputRef}
          id={inputId}
          name="one-time-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          value={code}
          onChange={(e) => onChange(e.target.value)}
          disabled={busy}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${hintId} ${errorId}` : hintId}
          className="w-full rounded-md border border-neutral-300 bg-white px-4 py-3 text-center font-mono text-2xl tracking-[0.4em] text-neutral-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 aria-[invalid=true]:border-red-600"
          style={{ colorScheme: "light" }}
        />
        <p id={hintId} className="text-sm text-neutral-600">
          {t("auth.emailStep.spamHint")}
        </p>
        {error && (
          <p id={errorId} role="alert" className="text-sm font-medium text-red-700">
            {error}
          </p>
        )}
        <p aria-live="polite" className="text-sm text-primary">
          {notice ?? ""}
        </p>
      </div>

      <Button type="submit" fullWidth loading={busy} disabled={code.length !== CODE_LENGTH}>
        {busy ? t("auth.emailStep.verifying") : t("auth.emailStep.verify")}
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onRestart()}
          className="min-h-11 rounded text-sm font-semibold text-neutral-700 hover:text-neutral-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {t("auth.emailStep.back")}
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0 || resending || busy}
          aria-disabled={cooldown > 0 || resending || busy}
          className="min-h-11 rounded text-sm font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:text-neutral-500 disabled:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          {cooldown > 0 ? t("auth.emailStep.resendIn", { seconds: cooldown }) : t("auth.emailStep.resend")}
        </button>
      </div>
    </form>
  );
}
